const DEFAULT_DIFFUSE_FILENAME = "craneIconTealInverse.png";
const DEFAULT_NORMAL_FILENAME = "defaultNormal.jpg";

export type TextureManagerArgs = {
  defaultDiffuseTextureFile?: string;
  defaultNormalTextureFile?: string;
};

export class TextureManager {
  private _textureStore: { [key: string]: WebGLTexture };
  private _defaultDiffuseFile: string;
  private _defaultNormalFile: string;

  constructor(args?: TextureManagerArgs) {
    this._textureStore = {};

    // check if defaults are set, if not set a reasonable one from public textures
    this._defaultDiffuseFile = args?.defaultDiffuseTextureFile ? args.defaultDiffuseTextureFile : DEFAULT_DIFFUSE_FILENAME
    this._defaultNormalFile = args?.defaultNormalTextureFile ? args.defaultNormalTextureFile : DEFAULT_NORMAL_FILENAME
  }

  async init(gl: WebGL2RenderingContext) {
    // load up the default textures
    const defaultDiffuse = await this.loadTexture(gl, this._defaultDiffuseFile);
    const defaultNormal = await this.loadTexture(gl, this._defaultNormalFile, gl.RGB, gl.RGB8);

    this._textureStore[DEFAULT_DIFFUSE_FILENAME] = defaultDiffuse;
    this._textureStore[DEFAULT_NORMAL_FILENAME] = defaultNormal;
  }

  getTexture(key?: string, type: "diffuse" | "normal" = "diffuse"): { name: string, texture: WebGLTexture } {
    if (!key || !(key in this._textureStore)) {
      console.warn(`unable to find texture '${key}', resorting to default texture`);
      // throw new Error(`unable to getTexture on key '${key}'`);
      // just load the default
      if (type === "diffuse") {
        return { name: DEFAULT_DIFFUSE_FILENAME, texture: this._textureStore[DEFAULT_DIFFUSE_FILENAME] };
      } else {
        return { name: DEFAULT_NORMAL_FILENAME, texture: this._textureStore[DEFAULT_NORMAL_FILENAME] };
      }
    }
    return { texture: this._textureStore[key], name: key };
  }

  listTextures(): string[] {
    return Object.keys(this._textureStore);
  }

  addTexture(key: string, texture: WebGLTexture) {
    if (key in this._textureStore) {
      throw new Error("texture by this key already exists");
    }
    this._textureStore[key] = texture;
  }

  deleteTexture(key: string) {
    if (!key || !(key in this._textureStore)) {
      throw new Error(`no texture with key '${key}'`);
    }
    delete this._textureStore[key];
  }

  loadTexture(
    gl: WebGL2RenderingContext,
    imgPath: string,
    format: GLenum = gl.RGBA,
    internalFormat: GLenum = gl.RGBA,
  ): Promise<WebGLTexture> {
    return new Promise(async (resolve, reject) => {
      const texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        internalFormat,
        1,
        1,
        0,
        format,
        gl.UNSIGNED_BYTE,
        new Uint8Array([255, 0, 0, 255]),
      ); // placeholder red pixel

      const image = new Image();
      image.src = imgPath;
      image.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          internalFormat,
          format,
          gl.UNSIGNED_BYTE,
          image,
        );
        gl.generateMipmap(gl.TEXTURE_2D);
        resolve(texture!);
      };
      image.onerror = reject;
    });
  }

  async loadTextureFromBuffer(
    gl: WebGL2RenderingContext,
    buffer: ArrayBuffer | Uint8Array,
    mimeType: string = "image/png",
    format: GLenum = gl.RGBA,
    internalFormat: GLenum = gl.RGBA
  ): Promise<WebGLTexture> {
    return new Promise((resolve, reject) => {
      const texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);

      // Placeholder 1x1 pixel
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        internalFormat,
        1,
        1,
        0,
        format,
        gl.UNSIGNED_BYTE,
        new Uint8Array([255, 0, 0, 255])
      );

      // Always create a Uint8Array view to satisfy Blob
      const blob = new Blob([new Uint8Array(buffer)], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const image = new Image();
      image.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, format, gl.UNSIGNED_BYTE, image);
        gl.generateMipmap(gl.TEXTURE_2D);

        URL.revokeObjectURL(url);
        resolve(texture!);
      };

      image.onerror = (err) => {
        console.error(err)
        URL.revokeObjectURL(url);
        reject(err);
      };

      image.src = url;
    });
  }
}